import { computed } from 'vue';

import { GlobalState } from '@/global';
import { WorkstationBridge } from '@/modules/workstation/helpers/bridge';
import { hasPermission } from '@/shared/helpers/permissions.helpers';
import { Auth } from '@/shared/services/auth.service';

export type SecurityTabId = 'passkey' | 'password' | 'totp' | 'fingerprints' | 'encryption';

export interface SecurityTab {
    id: SecurityTabId;
    label: string;
    available: boolean;
    /** Why this tab cannot be used; undefined when it can. */
    unavailableReason?: string;
}

/**
 * Every personal-security tab, each with whether this session can use it.
 *
 * Unavailable tabs are listed rather than hidden: a factor that silently disappears
 * gives no way to tell "your organization turned this off" apart from "the app is
 * broken". The reasons below are what the modal shows in place of the panel.
 *
 * The API is the source of truth for the local sign-in factors — `loginMethods`
 * already encodes whether the account is SSO-managed, which factors the tenant
 * permits, and the rule that a sign-in passkey excludes password and TOTP. Only the
 * constraints it cannot express are layered on here: the workstation/browser split
 * (a workstation login is a shared device login, not a personal one) and the
 * `core` permissions.
 */
const loginMethods = Auth.loginMethods;

const SharedDeviceReason = 'This is personal to a browser session. ZynoWorkstation is a shared device login.';
const NoClaimReason = 'The sign-in service did not report which methods this account can use.';

/**
 * A passkey is exclusive: once one exists the API refuses password and TOTP setup, so
 * those choices must not be offered even when their stored data still exists.
 */
const hasSignInPasskey = computed(() => loginMethods.value.passkey.configured);

export const canManageSignInPasskey = computed(
    () => !GlobalState.isWorkstation && (loginMethods.value.passkey.setupAllowed || loginMethods.value.passkey.configured)
);

// Setting a password also requires having signed in with one (or being on a workstation) —
// the same check the API makes before it will accept a new password.
export const canChangePassword = computed(
    () =>
        (GlobalState.isWorkstation || GlobalState.user.value?.authMech?.includes('password')) &&
        loginMethods.value.password.setupAllowed &&
        hasPermission('core', 'changePassword')
);

export const canManageTotp = computed(
    () => !GlobalState.isWorkstation && !hasSignInPasskey.value && (loginMethods.value.totp.setupAllowed || loginMethods.value.totp.configured)
);

export const canEnrollFingerprint = computed(
    () => GlobalState.isWorkstation && WorkstationBridge.devices.value.fingerprintReader && hasPermission('core', 'manageFingerprints')
);

export const canManageEncryption = computed(() => !GlobalState.isWorkstation && !!GlobalState.tenant.value);

function passkeyReason(): string {
    if (GlobalState.isWorkstation) return SharedDeviceReason;
    if (!Auth.hasLoginMethods.value) return NoClaimReason;
    return 'Your organization does not allow passkey sign-in for this account.';
}

function passwordReason(): string {
    if (!hasPermission('core', 'changePassword')) return 'You do not have permission to change your password.';
    if (hasSignInPasskey.value) return 'A sign-in passkey replaces your password, so there is no password to change.';
    if (!Auth.hasLoginMethods.value) return NoClaimReason;
    if (!loginMethods.value.password.setupAllowed) return 'This account signs in through your organization, which manages its password.';
    // setupAllowed, so the only thing left is how this session signed in.
    return 'Sign in with your password to change it.';
}

function totpReason(): string {
    if (GlobalState.isWorkstation) return SharedDeviceReason;
    if (hasSignInPasskey.value) return 'A sign-in passkey already covers this account, so authenticator codes are not used.';
    if (!Auth.hasLoginMethods.value) return NoClaimReason;
    return 'Your organization does not allow authenticator apps for this account.';
}

function fingerprintReason(): string {
    if (!GlobalState.isWorkstation) return 'Fingerprints are enrolled from ZynoWorkstation.';
    if (!WorkstationBridge.devices.value.fingerprintReader) return 'Attach a fingerprint reader and restart ZynoWorkstation.';
    return 'You do not have permission to manage fingerprints.';
}

function encryptionReason(): string {
    if (GlobalState.isWorkstation) return SharedDeviceReason;
    return 'Select a tenant to manage encryption.';
}

export const securityTabs = computed<SecurityTab[]>(() => [
    { id: 'passkey', label: 'Passkey', available: !!canManageSignInPasskey.value, unavailableReason: passkeyReason() },
    { id: 'password', label: 'Password', available: !!canChangePassword.value, unavailableReason: passwordReason() },
    { id: 'totp', label: 'Two-factor', available: !!canManageTotp.value, unavailableReason: totpReason() },
    { id: 'fingerprints', label: 'Fingerprints', available: !!canEnrollFingerprint.value, unavailableReason: fingerprintReason() },
    { id: 'encryption', label: 'Encryption', available: !!canManageEncryption.value, unavailableReason: encryptionReason() }
]);

/** The tabs this session can actually act on — what the modal opens to. */
export const availableSecurityTabs = computed<SecurityTab[]>(() => securityTabs.value.filter(tab => tab.available));

/** Whether the user menu should offer personal security settings at all. */
export const canOpenSecurity = computed(() => availableSecurityTabs.value.length > 0);
